import { ASnackbar } from '@adornis/adornis-components/a-snackbar';
import { AdornisRouter } from '@adornis/base/client/a-router';
import { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { DataLoadingController } from '@adornis/chemistry/controllers/data-loading-controller';
import { Loader } from '@adornis/chemistry/controllers/loader';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { css } from '@adornis/chemistry/directives/css';
import '@adornis/chemistry/elements/components/x-empty-placeholder';
import { ElementsFlex } from '@adornis/chemistry/elements/components/x-flex';
import '@adornis/germanium/components/ge-input';
import { GEInput } from '@adornis/germanium/components/ge-input.js';
import '@adornis/germanium/components/ge-section';
import { WTGUser } from 'db/wtg-user';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { tap } from 'rxjs/operators';
import './components/wtg-footer';
import './components/wtg-login';
import './components/wtg-navbar';
import { WTGNavbar } from './components/wtg-navbar';
import './components/wtg-post';
import './components/wtg-signup';
import './pages/wtg-blog';
import './pages/wtg-create-profile';
import './pages/wtg-events';
import './pages/wtg-faq';
import './pages/wtg-home';
import './pages/wtg-welcome-box';

@customElement('main-element')
export class MainElement extends ChemistryLitElement {
  _ = new Loader(this);

  @state() page = new ObservableController(
    this,
    AdornisRouter.get('page').pipe(
      tap(() => {
        this.scrollTo({ top: 0, behavior: 'smooth' });
        const navbar = this.shadowRoot?.querySelector('wtg-navbar') as WTGNavbar;
        if (!navbar) return;
        navbar.showMenu = false;
      }),
    ),
  );
  size = new SizeController(this);
  @state() isTop = true;
  user = new DataLoadingController(this, () => WTGUser.currentUser.whenReady);

  public render() {
    const navbar = html`
      <wtg-navbar
        .isTop=${this.isTop}
        @section-changed=${(e: CustomEvent) =>
          window.document.querySelector('main-element')?.scrollTo({ top: 0, behavior: 'smooth' })}
      >
      </wtg-navbar>
      ${!this.user.value || this.user.value.emailConfirmed
        ? nothing
        : html`
            <x-flex
              padding="md"
              center
              horizontal
              space="lg"
              ${css({ background: this.colors.accent, color: this.colors.on.accent })}
            >
              <ge-text> Your account has not been verified yet. </ge-text>
              <ge-button
                mode="text"
                icon="refresh"
                @click=${async () => {
                  await WTGUser.sendInviteMail(this.user.value!._id)('');
                  ASnackbar.show('sent! Please check your inbox.');
                }}
              >
                send email again
              </ge-button>
            </x-flex>
          `}
    `;
    return html`
      <x-flex ${css({ height: '100vh', width: '100%', position: 'relative' })}>
        ${this.size.isTablet
          ? html`
              <x-flex ${css({ position: 'absolute', top: '0', zIndex: '3', width: '100%', right: '0', left: '0' })}>
                ${navbar}
              </x-flex>
            `
          : navbar}

        <x-flex
          ${css({ height: '100%', width: '100vw', overflowY: 'auto', overflowX: 'hidden' })}
          @scroll=${(e: Event) => (this.isTop = (e.currentTarget as ElementsFlex).scrollTop <= 0)}
          @scroll-down=${e => {
            e.currentTarget.scrollBy({ top: 800, behavior: 'smooth' });
          }}
        >
          ${this.loadContent()}

          <x-flex ${css({ position: 'fixed', bottom: '40px', right: '40px' })}>
            <ge-socialmedia-popup></ge-socialmedia-popup>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private loadContent() {
    switch (this.page.value) {
      case '':
        return html`<wtg-home></wtg-home>`;
      case 'blog':
        return html`<wtg-blog></wtg-blog>`;
      case 'faq':
        return html`<wtg-faq></wtg-faq>`;
      case 'events':
        return html`<wtg-events></wtg-events>`;
      case 'login':
        return html`<wtg-login></wtg-login>`;
      case 'signup':
        return html`<wtg-signup></wtg-signup>`;

      case 'create_profile':
        return html` <wtg-create-profile> </wtg-create-profile> `;
      case 'welcome_box':
        return html`<wtg-welcome-box></wtg-welcome-box>`;

      case 'change-password':
        return this.changePassword();

      default:
        return html`
          <x-flex ${css({ height: '80vh' })} flex-none center>
            <x-empty-placeholder no-wrapper .img=${'question_answer'} .text=${'Nothing here...'}></x-empty-placeholder>
          </x-flex>
        `;
    }
  }

  private changePassword() {
    return html`
      <x-flex crossaxis-center center ${css({ height: '400px' })}>
        <x-flex space="md">
          <x-h3> Set a new Password </x-h3>
          <ge-input id="pw1" placeholder="New Password" type="password"></ge-input>
          <ge-input id="pw2" placeholder="Repeat Password" type="password"></ge-input>
          <ge-button
            icon="check"
            @click=${async () => {
              const pw1 = (this.shadowRoot?.querySelector('#pw1') as Maybe<GEInput>).value.value;
              const pw2 = (this.shadowRoot?.querySelector('#pw2') as Maybe<GEInput>).value.value;

              if (pw1 !== pw2) return ASnackbar.show('Passwords do not match. ');

              const token = AdornisRouter.get().queryParams.getOnce('token');
              if (!token) return ASnackbar.show('Invalid Token, use the link you received in your email!');
              try {
                await WTGUser.changePasswordWithToken(token, pw1)('');
                ASnackbar.show('Success!');
                AdornisRouter.goTo('/login');
                AdornisRouter.get().queryParams.delete('token');
              } catch (e) {
                ASnackbar.show(`Error: ${(e as Error).message}`);
              }
            }}
          >
            Confirm
          </ge-button>
        </x-flex>
      </x-flex>
    `;
  }
}
